// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the CRDT beside the document
//  ---------------------------------------------------------------------------
//  E5. md-merge.ts reconciles a day by comparing wall clocks and picking a
//  winner PER RECORD. Two devices editing two different fields of one record
//  therefore lose one of the edits, silently, with nothing reported anywhere.
//  This module ends that, without becoming the thing the app stores.
//
//  ── WHY THIS IS NOT THE DOCUMENT ───────────────────────────────────────────
//  The plan said a Yjs document would live in the row's value. It cannot.
//  mdSearchDoc (md-search.ts) is handed the raw stored string for every key on
//  every flush and its second line is a JSON.parse whose catch returns null — a
//  null writes no search row. An opaque value would take every record in every
//  day out of the Finder, silently, with nothing to repair it. Backup and
//  export read through the same parse.
//
//  So the readable document stays exactly what it is, and this state rides in
//  its own key beside it — the way `md:tombstones:<day>` already does.
//
//  ── EVERY FUNCTION ANSWERS null RATHER THAN THROWING ───────────────────────
//  A null means "the CRDT cannot answer this", and the caller falls back to the
//  clock merge. That is the property that makes this safe to ship: a state that
//  is missing, stale, truncated, corrupt or hostile costs a device TODAY'S
//  BEHAVIOUR — never a day of somebody's writing. It is also how the migration
//  works, because every document written before this existed has no state at
//  all and must simply carry on.
//
//  ── HOUSEKEEPING STAYS ON ──────────────────────────────────────────────────
//  Yjs is constructed with its default garbage collection, which discards the
//  CONTENT of deleted items and keeps only a marker. That is deliberate and it
//  is a decision, not an oversight: with it off, a state can restore its own
//  history but grows with every character ever typed and can never be reset.
//  Version history is a separate append-only log for exactly this reason.
//  Keeping this state small and DISPOSABLE is what
//  lets it be thrown away and reseeded from the document whenever it misbehaves.
//
//  ── NOTHING HERE IS BUILT AT BOOT ──────────────────────────────────────────
//  A Y.Doc is constructed for one document at the moment that document is
//  written or reconciled, and never otherwise. Boot went from 25 seconds to
//  instant by not touching everything on the way up; building sync machinery
//  for 1,773 days at launch would hand that straight back.
// ════════════════════════════════════════════════════════════════════════════

/** A record as it lives inside a day document. Identity is `uid`; every other
    field is the UI's business and is carried through untouched. */
interface MdCrdtRecord {
  uid: string;
  min?: number;
  [field: string]: unknown;
}

(function (g: any) {
  'use strict';

  /* The map inside a state, named rather than positional so the two shapes can
     never be read as each other.

     `records` is a LIST of things with identity — a day. `doc` is ONE thing
     whose fields are the answers — a database row. Same engine, same merge,
     same encoding; only what the top-level map is keyed by differs. */
  const RECORDS = 'records';
  const DOC = 'doc';

  const Y = (): any => (g.MDYjs && typeof g.MDYjs.Doc === 'function' ? g.MDYjs : null);

  const usable = (r: unknown): r is MdCrdtRecord =>
    !!r && typeof r === 'object' && !Array.isArray(r)
    && typeof (r as any).uid === 'string' && (r as any).uid.length > 0;

  /* ── bytes ⇄ text ─────────────────────────────────────────────────────────
     A Yjs update is binary and the document it rides beside is JSON, so it is
     stored base64. Encoded in chunks because String.fromCharCode.apply on a
     100 KB array overflows the call stack — a failure that only appears once
     somebody's account is big enough to matter. */
  const CHUNK = 0x2000;

  function toText(bytes: Uint8Array): string | null {
    try {
      let out = '';
      for (let i = 0; i < bytes.length; i += CHUNK) {
        out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
      }
      return g.btoa(out);
    } catch (e) { return null; }
  }

  function toBytes(text: unknown): Uint8Array | null {
    if (typeof text !== 'string' || text === '') return null;
    try {
      const raw = g.atob(text);
      const out = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
      return out.length ? out : null;
    } catch (e) { return null; }
  }

  /** A document holding exactly `state`, or null if it cannot be read. */
  function open(state: unknown): any {
    const yjs = Y();
    if (!yjs) return null;
    const bytes = toBytes(state);
    if (!bytes) return null;
    try {
      const doc = new yjs.Doc();
      yjs.applyUpdate(doc, bytes);
      return doc;
    } catch (e) { return null; }
  }

  const encode = (doc: any): string | null => {
    try { return toText(g.MDYjs.encodeStateAsUpdate(doc)); } catch (e) { return null; }
  };

  const isPlain = (v: unknown): v is Record<string, unknown> => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
    const proto = Object.getPrototypeOf(v);
    return proto === Object.prototype || proto === null;
  };

  const isMap = (v: any): boolean => !!v && typeof v === 'object'
    && typeof v.set === 'function' && typeof v.get === 'function'
    && typeof v.keys === 'function' && typeof v.delete === 'function';

  /* Write one object's fields into a map, so two devices touching two different
     FIELDS of one thing both survive. That granularity is the whole point; a
     thing stored as one opaque value would merge no better than the clock does.

     A NESTED OBJECT BECOMES A NESTED MAP, which is what carries the granularity
     down to where a database keeps its answers: `row.values.<propertyId>` is a
     cell, and two people filling in two different columns is the ordinary case,
     not the exotic one.

     AN ARRAY IS A LEAF, deliberately. A multi-select's option ids are one answer
     given at one moment; merging them element-wise would produce a selection
     nobody made. The cost is that a list of identified things — a row's
     comments — stays whole-value last-write-wins, which is what it is today.

     A field whose value has not changed is not written. Yjs would otherwise
     record an operation for every field on every save, and a document that
     grows on idle saves is a document that can never be reasoned about. */
  function putMap(yjs: any, map: any, fields: Record<string, unknown>): void {
    for (const key of Object.keys(fields)) {
      const next = fields[key];
      /* Present-and-undefined is absent: it is what JSON does with it, so
         storing one would put a field in the state that no reader can see. */
      if (next === undefined) { if (map.has(key)) map.delete(key); continue; }
      if (isPlain(next)) {
        let child = map.get(key);
        if (!isMap(child)) { child = new yjs.Map(); map.set(key, child); }
        putMap(yjs, child, next);
        continue;
      }
      const held = map.get(key);
      let same = false;
      /* A map held where a leaf is wanted is never "the same": the shape
         changed, and the leaf has to replace it. */
      if (!isMap(held)) {
        try { same = JSON.stringify(held) === JSON.stringify(next); } catch (e) { same = false; }
      }
      if (!same) map.set(key, next);
    }
    /* A field the object no longer carries has been cleared, not left alone. */
    for (const key of [...map.keys()]) if (!(key in fields)) map.delete(key);
  }

  /** A record's fields, without the identity the map is already keyed by. */
  function fieldsOf(record: MdCrdtRecord): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(record)) if (key !== 'uid') out[key] = record[key];
    return out;
  }

  function readAll(doc: any): MdCrdtRecord[] {
    const records = doc.getMap(RECORDS);
    const out: MdCrdtRecord[] = [];
    for (const uid of [...records.keys()]) {
      const held = records.get(uid);
      if (!held || typeof held.toJSON !== 'function') continue;
      out.push({ ...held.toJSON(), uid });
    }
    /* The same deterministic order md-merge.ts settles on. Two devices holding
       the same set in a different order would keep handing each other a
       "changed" document forever. */
    out.sort((a, b) => {
      const ma = typeof a.min === 'number' && Number.isFinite(a.min) ? a.min : -1;
      const mb = typeof b.min === 'number' && Number.isFinite(b.min) ? b.min : -1;
      return ma !== mb ? ma - mb : (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0);
    });
    return out;
  }

  /** A fresh state describing exactly these records. */
  function mdCrdtSeed(records: unknown): string | null {
    const yjs = Y();
    if (!yjs || !Array.isArray(records)) return null;
    try {
      const doc = new yjs.Doc();
      const map = doc.getMap(RECORDS);
      doc.transact(() => {
        for (const r of records) {
          if (!usable(r)) continue;                          // no identity: nothing to merge into
          const fields = new yjs.Map();
          map.set(r.uid, fields);
          putMap(yjs, fields, fieldsOf(r));
        }
      });
      return encode(doc);
    } catch (e) { return null; }
  }

  /** `state`, brought up to date with `records`. Null if the state is unreadable
      — the caller then reseeds or falls back, and either is safe. */
  function mdCrdtApply(state: unknown, records: unknown): string | null {
    const yjs = Y();
    const doc = open(state);
    if (!yjs || !doc || !Array.isArray(records)) return null;
    try {
      const map = doc.getMap(RECORDS);
      doc.transact(() => {
        const kept = new Set<string>();
        for (const r of records) {
          if (!usable(r)) continue;
          kept.add(r.uid);
          let fields = map.get(r.uid);
          if (!fields || typeof fields.set !== 'function') {
            fields = new yjs.Map();
            map.set(r.uid, fields);
          }
          putMap(yjs, fields, fieldsOf(r));
        }
        /* Gone from the array means deleted by the user. Yjs carries its own
           delete set, so this needs no tombstone of ours to survive a merge. */
        for (const uid of [...map.keys()]) if (!kept.has(uid)) map.delete(uid);
      });
      return encode(doc);
    } catch (e) { return null; }
  }

  /** What two states agree on. Null if EITHER cannot be read — a half-merge is
      worse than falling back, because it looks like an answer. */
  function mdCrdtMerge(local: unknown, remote: unknown): string | null {
    const yjs = Y();
    const a = toBytes(local), b = toBytes(remote);
    if (!yjs || !a || !b) return null;
    try {
      const doc = new yjs.Doc();
      yjs.applyUpdate(doc, a);
      yjs.applyUpdate(doc, b);
      return encode(doc);
    } catch (e) { return null; }
  }

  /** The records a state holds, or null if it holds nothing readable. */
  function mdCrdtRecords(state: unknown): MdCrdtRecord[] | null {
    const doc = open(state);
    if (!doc) return null;
    try { return readAll(doc); } catch (e) { return null; }
  }

  /* ── the same engine, over one object ────────────────────────────────────
     A database row is not a list, it is a single document whose answers live
     one level down under `values`. mdCrdtMerge serves both shapes unchanged —
     it merges updates, and knows nothing about what is inside them. */

  /** A fresh state describing exactly this object. */
  function mdCrdtSeedFields(value: unknown): string | null {
    const yjs = Y();
    if (!yjs || !isPlain(value)) return null;
    try {
      const doc = new yjs.Doc();
      const map = doc.getMap(DOC);
      doc.transact(() => putMap(yjs, map, value));
      return encode(doc);
    } catch (e) { return null; }
  }

  /** `state`, brought up to date with `value`. */
  function mdCrdtApplyFields(state: unknown, value: unknown): string | null {
    const yjs = Y();
    const doc = open(state);
    if (!yjs || !doc || !isPlain(value)) return null;
    try {
      doc.transact(() => putMap(yjs, doc.getMap(DOC), value));
      return encode(doc);
    } catch (e) { return null; }
  }

  /** The object a state holds, or null if it holds nothing readable. */
  function mdCrdtFields(state: unknown): Record<string, unknown> | null {
    const doc = open(state);
    if (!doc) return null;
    try {
      const out = doc.getMap(DOC).toJSON();
      return isPlain(out) ? out : null;
    } catch (e) { return null; }
  }

  g.mdCrdtSeed = mdCrdtSeed;
  g.mdCrdtSeedFields = mdCrdtSeedFields;
  g.mdCrdtApplyFields = mdCrdtApplyFields;
  g.mdCrdtFields = mdCrdtFields;
  g.mdCrdtApply = mdCrdtApply;
  g.mdCrdtMerge = mdCrdtMerge;
  g.mdCrdtRecords = mdCrdtRecords;

  // CommonJS escape hatch, following md-merge.ts's lead, so a node test runner
  // can require this without a DOM.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdCrdtSeed, mdCrdtApply, mdCrdtMerge, mdCrdtRecords,
      mdCrdtSeedFields, mdCrdtApplyFields, mdCrdtFields };
  }
})(typeof window !== 'undefined' ? window : globalThis);
